import { useSelector } from "react-redux";
import { createContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { RootState } from "../redux/store";
import { LoadingSpinner } from "../components/ui";
import { UserData } from "../services/auth.service";

interface AuthContextType {
  persist: boolean;
  isLoading: boolean;
  user: UserData | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  setPersist: (persist: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [persist, setPersist] = useState(
    JSON.parse(localStorage.getItem("persist") || "true")
  );

  const { isAuthenticated, user, lang, accessToken } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    // Check if we're on an auth route
    const isAuthRoute = [
      `/${lang}/auth/login`,
      `/${lang}/auth/register`,
      `/${lang}/auth/verify-email`,
      `/${lang}/auth/reset-password`,
      `/${lang}/auth/forgot-password`,
    ].includes(location.pathname);

    const isProtectedRoute = [`/${lang}/${user?.role}`].includes(
      location.pathname
    );

    if (isLoading) {
      setIsLoading(false);
    }

    // If not authenticated and on a protected route, redirect to login
    if (!isAuthenticated && isProtectedRoute) {
      navigate(`/${lang}/auth/login`, { replace: true });
    }

    // If authenticated and on auth routes, redirect to dashboard
    if (isAuthenticated && isAuthRoute) {
      // Check if user has a role and redirect to appropriate dashboard
      if (user?.role) {
        const dashboardPath =
          user.role === "admin"
            ? `/${lang}/admin`
            : `/${lang}/${user.role}/dashboard`;
        navigate(dashboardPath, { replace: true });
      }
    }
  }, [
    isAuthenticated,
    location.pathname,
    navigate,
    user?.role,
    isLoading,
    lang,
  ]);

  const value = {
    user,
    persist,
    isLoading,
    setPersist,
    accessToken,
    isAuthenticated,
  };

  if (isLoading) {
    return <LoadingSpinner size="lg" />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
